import { useState } from 'react';
import { Loader2, Play, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function fmtDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

const TABS = [
  { key: 'staleness', label: 'Sync Staleness' },
  { key: 'webhook', label: 'Webhook Log' },
  { key: 'reservations', label: 'Reservations' },
  { key: 'synclog', label: 'Sync Log' },
  { key: 'property', label: 'Property Sync' },
  { key: 'bookingprobe', label: 'Booking Probe' },
  { key: 'routes', label: 'Routes' },
];

/* ------------------------------------------------------------------ */
/*  Tab 1: Sync Staleness (last-sync-per-property)                     */
/* ------------------------------------------------------------------ */

function StalenessTab() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.lastSyncPerProperty();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const stalenessClass = (row) => {
    if (row.is_stale) return 'text-red-600 dark:text-red-400 font-medium';
    return '';
  };

  return (
    <div className="space-y-4">
      <Button onClick={run} disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
        Run
      </Button>

      {result && (
        <div className="space-y-4">
          <div className="flex items-center gap-4 text-sm">
            <span>Properties: {fmtInt(result.total_properties)}</span>
            {result.stale_count > 0 ? (
              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4" />
                {result.stale_count} stale ({'>'}{result.stale_cutoff_days}d)
              </span>
            ) : (
              <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                All current
              </span>
            )}
          </div>

          {result.rows && result.rows.length > 0 && (
            <div className="overflow-x-auto border rounded-lg max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0">
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">Property</th>
                    <th className="text-left px-2 py-1.5 font-medium">Entity</th>
                    <th className="text-right px-2 py-1.5 font-medium">Reservations</th>
                    <th className="text-left px-2 py-1.5 font-medium">Last sync</th>
                    <th className="text-center px-2 py-1.5 font-medium">Stale?</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r) => (
                    <tr key={r.property_id} className={`border-b last:border-0 ${stalenessClass(r)}`}>
                      <td className="px-2 py-1">
                        <div className="font-mono text-[10px] text-muted-foreground">{r.property_id}</div>
                        <div>{r.property_name || '-'}</div>
                      </td>
                      <td className="px-2 py-1">{r.entity_name || r.entity_id || '-'}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmtInt(r.reservation_count)}</td>
                      <td className="px-2 py-1">{fmtDate(r.max_synced_at)}</td>
                      <td className="px-2 py-1 text-center">
                        {r.is_stale ? (
                          <AlertTriangle className="h-3.5 w-3.5 text-red-500 inline" />
                        ) : (
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-600 inline" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 2: Webhook Log                                                 */
/* ------------------------------------------------------------------ */

function WebhookLogTab() {
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(20);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.webhookLogTail({ limit });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <input
          type="number"
          value={limit}
          onChange={(e) => setLimit(Math.max(1, Math.min(500, parseInt(e.target.value, 10) || 20)))}
          min={1}
          max={500}
          className="border rounded px-3 py-1.5 text-sm w-20 bg-background"
        />
        <Button onClick={run} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          Run
        </Button>
      </div>

      {result && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {result.count} entries from {result.source_collection}
          </p>
          {result.entries && result.entries.length > 0 && (
            <div className="overflow-x-auto border rounded-lg max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0">
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">Received</th>
                    <th className="text-left px-2 py-1.5 font-medium">Event</th>
                    <th className="text-left px-2 py-1.5 font-medium">Property</th>
                    <th className="text-left px-2 py-1.5 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {result.entries.map((entry, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-2 py-1 whitespace-nowrap">{fmtDate(entry.received_at)}</td>
                      <td className="px-2 py-1">
                        <Badge variant="outline" className="text-[10px]">
                          {entry.event_type || entry.event || '-'}
                        </Badge>
                      </td>
                      <td className="px-2 py-1 font-mono">{entry.property_id || '-'}</td>
                      <td className="px-2 py-1">
                        <details className="text-[10px]">
                          <summary className="cursor-pointer">payload</summary>
                          <pre className="mt-1 border rounded p-2 overflow-x-auto bg-muted/30 max-h-32 overflow-y-auto whitespace-pre-wrap">
                            {JSON.stringify(entry, null, 2)}
                          </pre>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result.entries?.length === 0 && (
            <p className="text-sm text-muted-foreground">No webhook log entries found.</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 3: Recent Reservations                                         */
/* ------------------------------------------------------------------ */

function RecentReservationsTab() {
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(20);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.recentReservations({ limit });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <input
          type="number"
          value={limit}
          onChange={(e) => setLimit(Math.max(1, Math.min(500, parseInt(e.target.value, 10) || 20)))}
          min={1}
          max={500}
          className="border rounded px-3 py-1.5 text-sm w-20 bg-background"
        />
        <Button onClick={run} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          Run
        </Button>
      </div>

      {result && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {result.count} docs from {result.source_collection}
          </p>
          {result.docs && result.docs.length > 0 && (
            <div className="overflow-x-auto border rounded-lg max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0">
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">ID</th>
                    <th className="text-left px-2 py-1.5 font-medium">Property</th>
                    <th className="text-left px-2 py-1.5 font-medium">Guest</th>
                    <th className="text-left px-2 py-1.5 font-medium">Check-in</th>
                    <th className="text-left px-2 py-1.5 font-medium">Status</th>
                    <th className="text-left px-2 py-1.5 font-medium">Source</th>
                    <th className="text-left px-2 py-1.5 font-medium">Synced</th>
                  </tr>
                </thead>
                <tbody>
                  {result.docs.map((doc, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono">{doc.uplisting_id || '-'}</td>
                      <td className="px-2 py-1">{doc.property_name || doc.property_id || '-'}</td>
                      <td className="px-2 py-1">{doc.guest_name || '-'}</td>
                      <td className="px-2 py-1">{doc.check_in || '-'}</td>
                      <td className="px-2 py-1">
                        <Badge variant="outline" className="text-[10px]">{doc.status || '-'}</Badge>
                      </td>
                      <td className="px-2 py-1">
                        {doc.webhook_event ? (
                          <Badge variant="outline" className="text-[10px] border-blue-400 text-blue-600">webhook</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">sync</Badge>
                        )}
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap">{fmtDate(doc.synced_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result.docs?.length === 0 && (
            <p className="text-sm text-muted-foreground">No reservations found.</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 4: Sync Log                                                    */
/* ------------------------------------------------------------------ */

function SyncLogTab() {
  const [loading, setLoading] = useState(false);
  const [propertyId, setPropertyId] = useState('');
  const [limit, setLimit] = useState(20);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const params = { limit };
      if (propertyId.trim()) params.property_id = propertyId.trim();
      const { data } = await diagnosticsAPI.syncLogTail(params);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          placeholder="Property ID (optional)"
          className="border rounded px-3 py-1.5 text-sm w-48 bg-background"
        />
        <input
          type="number"
          value={limit}
          onChange={(e) => setLimit(Math.max(1, Math.min(200, parseInt(e.target.value, 10) || 20)))}
          min={1}
          max={200}
          className="border rounded px-3 py-1.5 text-sm w-20 bg-background"
        />
        <Button onClick={run} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          Run
        </Button>
      </div>

      {result && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {result.count} entries from {result.source_collection}
            {result.property_id && <span> (filtered: {result.property_id})</span>}
          </p>
          {result.entries && result.entries.length > 0 && (
            <div className="overflow-x-auto border rounded-lg max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0">
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">Timestamp</th>
                    <th className="text-left px-2 py-1.5 font-medium">Status</th>
                    <th className="text-left px-2 py-1.5 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {result.entries.map((entry, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-2 py-1 whitespace-nowrap">{fmtDate(entry.timestamp)}</td>
                      <td className="px-2 py-1">
                        <Badge
                          variant={entry.status === 'success' ? 'default' : 'destructive'}
                          className={entry.status === 'success' ? 'bg-green-600 hover:bg-green-700' : ''}
                        >
                          {entry.status || '-'}
                        </Badge>
                      </td>
                      <td className="px-2 py-1">
                        <details className="text-[10px]">
                          <summary className="cursor-pointer">details</summary>
                          <pre className="mt-1 border rounded p-2 overflow-x-auto bg-muted/30 max-h-32 overflow-y-auto whitespace-pre-wrap">
                            {JSON.stringify(entry, null, 2)}
                          </pre>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result.entries?.length === 0 && (
            <p className="text-sm text-muted-foreground">No sync log entries found.</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 4: Property Sync State                                         */
/* ------------------------------------------------------------------ */

function PropertySyncTab() {
  const [loading, setLoading] = useState(false);
  const [propertyId, setPropertyId] = useState('');
  const [result, setResult] = useState(null);

  const run = async () => {
    const pid = propertyId.trim();
    if (!pid) {
      toast.error('Enter a property ID');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.propertySyncState({ property_id: pid });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') run();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Property ID (required)"
          className="border rounded px-3 py-1.5 text-sm w-48 bg-background"
        />
        <Button onClick={run} disabled={loading || !propertyId.trim()}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          Run
        </Button>
      </div>

      {result && (
        <div className="space-y-4">
          {/* Property info */}
          {result.property_doc ? (
            <div className="border rounded-lg p-3 space-y-1">
              <p className="text-sm font-medium">{result.property_doc.name || result.property_id}</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <span className="text-muted-foreground">Entity</span>
                <span>{result.property_doc.entity_name || result.property_doc.entity_id || '-'}</span>
                <span className="text-muted-foreground">Reservations</span>
                <span className="tabular-nums">{fmtInt(result.reservation_count)}</span>
                <span className="text-muted-foreground">Property last sync</span>
                <span>{fmtDate(result.property_max_synced_at)}</span>
                <span className="text-muted-foreground">Fleet last sync</span>
                <span>{fmtDate(result.fleet_max_synced_at)}</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4" />
              No property document found for ID: {result.property_id}
            </div>
          )}

          {/* Recent reservations */}
          {result.recent_reservations && result.recent_reservations.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Recent reservations</p>
              <div className="overflow-x-auto border rounded-lg max-h-60 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0">
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">ID</th>
                      <th className="text-left px-2 py-1.5 font-medium">Check-in</th>
                      <th className="text-left px-2 py-1.5 font-medium">Status</th>
                      <th className="text-left px-2 py-1.5 font-medium">Synced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.recent_reservations.map((r, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{r.uplisting_id || '-'}</td>
                        <td className="px-2 py-1">{r.check_in || '-'}</td>
                        <td className="px-2 py-1">
                          <Badge variant="outline" className="text-[10px]">{r.status || '-'}</Badge>
                        </td>
                        <td className="px-2 py-1">{fmtDate(r.synced_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab: Booking Probe (single booking trace + 90-day census)          */
/* ------------------------------------------------------------------ */

function VerdictBadge({ verdict }) {
  if (!verdict) return null;
  const isGood = verdict.startsWith('would_ingest');
  const Icon = isGood ? CheckCircle2 : AlertTriangle;
  return (
    <div className={`flex items-start gap-2 text-sm ${isGood ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <span className="font-medium">{verdict}</span>
    </div>
  );
}

function BookingProbeTab() {
  const [bookingId, setBookingId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const [censusLoading, setCensusLoading] = useState(false);
  const [census, setCensus] = useState(null);

  const runProbe = async () => {
    const bid = bookingId.trim();
    if (!bid) {
      toast.error('Enter a booking ID');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const params = { booking_id: bid };
      if (propertyId.trim()) params.property_id = propertyId.trim();
      const { data } = await diagnosticsAPI.bookingProbe(params);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runCensus = async () => {
    setCensusLoading(true);
    setCensus(null);
    try {
      const { data } = await diagnosticsAPI.bookingCensus({ days: 90 });
      setCensus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCensusLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') runProbe();
  };

  return (
    <div className="space-y-6">
      {/* Single-booking probe */}
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Traces one booking id through the exact sync-path steps in order and reports
          the first condition that would drop it. Read-only, no writes.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="text"
            value={bookingId}
            onChange={(e) => setBookingId(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Booking ID (required)"
            className="border rounded px-3 py-1.5 text-sm w-48 bg-background"
          />
          <input
            type="text"
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Property ID (optional, speeds up search)"
            className="border rounded px-3 py-1.5 text-sm w-64 bg-background"
          />
          <Button onClick={runProbe} disabled={loading || !bookingId.trim()}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            Run
          </Button>
        </div>

        {result && (
          <div className="space-y-4 border rounded-lg p-3">
            <VerdictBadge verdict={result.verdict} />

            <div className="grid grid-cols-2 gap-2 text-xs">
              <span className="text-muted-foreground">Already has FA doc</span>
              <span>{result.already_has_db_doc ? 'yes' : 'no'}</span>
              <span className="text-muted-foreground">API returns booking</span>
              <span>{result.api_returns_booking ? 'yes' : 'no'}</span>
              <span className="text-muted-foreground">Found on property</span>
              <span className="font-mono">{result.found_on_property_id || '-'}</span>
              <span className="text-muted-foreground">Properties scanned</span>
              <span>
                {result.search_scope?.properties_scanned}
                {result.search_scope?.properties_scan_capped_at_300 && ' (capped at 300)'}
              </span>
            </div>

            {result.search_scope?.properties_with_api_error?.length > 0 && (
              <div className="border-t pt-2 space-y-1">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                  API errors -- absence is NOT proven for these properties
                </p>
                <div className="space-y-1">
                  {result.search_scope.properties_with_api_error.map((e) => (
                    <div key={e.property_id} className="text-xs font-mono text-amber-600 dark:text-amber-400">
                      {e.property_id}: HTTP {e.http_status ?? '-'} {typeof e.body === 'string' ? e.body : JSON.stringify(e.body)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.property && (
              <div className="border-t pt-2 space-y-1">
                <p className="text-xs font-medium">Property resolution</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-muted-foreground">Present in uplisting_properties</span>
                  <span>{result.property.present_in_uplisting_properties ? 'yes' : 'no'}</span>
                  <span className="text-muted-foreground">Entity</span>
                  <span>{result.property.entity_name || '-'} {result.property.entity_is_unmapped_default && (
                    <Badge variant="outline" className="text-[10px] ml-1 border-amber-400 text-amber-600">default</Badge>
                  )}</span>
                  <span className="text-muted-foreground">Excluded from portfolio</span>
                  <span>{String(result.property.excluded_from_portfolio)}</span>
                </div>
              </div>
            )}

            {result.quarantine_check && (
              <div className="border-t pt-2 space-y-1">
                <p className="text-xs font-medium">Quarantine simulation (gross_revenue)</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-muted-foreground">Would quarantine</span>
                  <span>{result.quarantine_check.quarantined ? 'yes' : 'no'}</span>
                  <span className="text-muted-foreground">gross_revenue</span>
                  <span>{result.quarantine_check.gross_revenue ?? '-'}</span>
                </div>
              </div>
            )}

            {result.properties_pagination_check && (
              <div className="border-t pt-2 space-y-1">
                <p className="text-xs font-medium">Properties list pagination check</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-muted-foreground">Returned by API (single page)</span>
                  <span>{result.properties_pagination_check.properties_count_returned_by_api ?? '-'}</span>
                  <span className="text-muted-foreground">In db.uplisting_properties</span>
                  <span>{result.properties_pagination_check.properties_count_in_db ?? '-'}</span>
                  <span className="text-muted-foreground">Pagination keys seen</span>
                  <span>
                    {(result.properties_pagination_check.pagination_metadata_keys_present_in_response || []).join(', ') || 'none'}
                  </span>
                </div>
              </div>
            )}

            <details className="text-[10px]">
              <summary className="cursor-pointer text-muted-foreground">full response</summary>
              <pre className="mt-1 border rounded p-2 overflow-x-auto bg-muted/30 max-h-96 overflow-y-auto whitespace-pre-wrap">
                {JSON.stringify(result, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>

      {/* 90-day census */}
      <div className="space-y-3 border-t pt-4">
        <p className="text-xs text-muted-foreground">
          Blast-radius census: last 90 days, live Uplisting API bookings vs FA docs, per property.
        </p>
        <Button onClick={runCensus} disabled={censusLoading} variant="outline">
          {censusLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          Run 90-day census
        </Button>

        {census && (
          <div className="space-y-2">
            <div className="flex items-center gap-4 text-sm">
              <span>Properties scanned: {fmtInt(census.properties_scanned)}</span>
              {census.total_missing_from_fa > 0 ? (
                <span className="flex items-center gap-1 text-red-600 dark:text-red-400 font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  {census.total_missing_from_fa} missing from FA
                </span>
              ) : (
                <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  No divergence
                </span>
              )}
            </div>
            {census.properties_with_api_error?.length > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                API errors for: {census.properties_with_api_error.join(', ')}
              </p>
            )}

            {census.diverging_properties?.length > 0 && (
              <div className="overflow-x-auto border rounded-lg max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0">
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Property</th>
                      <th className="text-right px-2 py-1.5 font-medium">API count</th>
                      <th className="text-right px-2 py-1.5 font-medium">FA count</th>
                      <th className="text-right px-2 py-1.5 font-medium">Missing</th>
                      <th className="text-left px-2 py-1.5 font-medium">Missing IDs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {census.diverging_properties.map((r) => (
                      <tr key={r.property_id} className="border-b last:border-0">
                        <td className="px-2 py-1">
                          <div className="font-mono text-[10px] text-muted-foreground">{r.property_id}</div>
                          <div>{r.property_name || '-'}</div>
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtInt(r.api_booking_count)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtInt(r.db_booking_count)}</td>
                        <td className="px-2 py-1 text-right tabular-nums text-red-600 dark:text-red-400">{fmtInt(r.missing_from_fa_count)}</td>
                        <td className="px-2 py-1 font-mono text-[10px]">
                          {r.missing_from_fa_ids.join(', ')}
                          {r.missing_from_fa_truncated && ' …'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 6: Route Introspection                                         */
/* ------------------------------------------------------------------ */

function RoutesTab() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.routeIntrospection();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Button onClick={run} disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
        Run
      </Button>

      {result && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {result.count} Uplisting routes mounted
          </p>
          {result.routes && result.routes.length > 0 && (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">Path</th>
                    <th className="text-left px-2 py-1.5 font-medium">Methods</th>
                    <th className="text-left px-2 py-1.5 font-medium">Function</th>
                  </tr>
                </thead>
                <tbody>
                  {result.routes.map((r, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono">{r.path}</td>
                      <td className="px-2 py-1">
                        {(r.methods || []).map((m) => (
                          <Badge key={m} variant="outline" className="text-[10px] mr-1">{m}</Badge>
                        ))}
                      </td>
                      <td className="px-2 py-1 font-mono text-muted-foreground">{r.endpoint_function || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main panel                                                         */
/* ------------------------------------------------------------------ */

export function UplistingDiagnosticsPanel() {
  const [activeTab, setActiveTab] = useState('staleness');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Uplisting Sync Diagnostics</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Detailed sync pipeline tools: staleness, webhook log, sync log, property drill-down.
        </p>

        {/* Tab row */}
        <div className="flex gap-1 flex-wrap">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'staleness' && <StalenessTab />}
        {activeTab === 'webhook' && <WebhookLogTab />}
        {activeTab === 'reservations' && <RecentReservationsTab />}
        {activeTab === 'synclog' && <SyncLogTab />}
        {activeTab === 'property' && <PropertySyncTab />}
        {activeTab === 'bookingprobe' && <BookingProbeTab />}
        {activeTab === 'routes' && <RoutesTab />}
      </CardContent>
    </Card>
  );
}
